<template>
  <!-- Loading -->
  <transition name="fade">
    <page-loader v-if="loading" />
  </transition>

  <!-- Normal app content -->
  <template v-if="!loading && !sessionError">
    <template
      v-if="
        $store.state.app.showDashboardNav &&
        userHasAccess($store.state.user.groups)
      "
    >
      <dashboard-header />
    </template>
    <main
      class="main-content"
      :class="{
        'main-content--dashboard': $store.state.app.showDashboardNav,
        'main-content--basic': !$store.state.app.showDashboardNav,
      }"
    >
      <template
        v-if="
          $store.state.app.showDashboardNav &&
          userHasAccess($store.state.user.groups)
        "
      >
        <main-nav componentClass="main-nav--desktop" />
      </template>
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>
    <cookie-consent />
  </template>

  <!-- Cookies blocked -->
  <template v-else-if="!loading && sessionError === 'cookies-blocked'">
    <main class="main-content main-content--basic">
      <div class="error-banner">
        <h1>Cookies are blocked</h1>
        <p>
          Your browser is blocking cookies for this site. Please enable cookies
          for <strong>localhost</strong> (or this domain) and try logging in
          again.
        </p>
      </div>
    </main>
  </template>

  <!-- Login required -->
  <template v-else-if="!loading && sessionError === 'login-required'">
    <main class="main-content main-content--basic">
      <div class="error-banner">
        <h1>Please sign in</h1>
        <p>
          Your session has expired or could not be verified. Sign in again to
          continue.
        </p>
        <button type="button" class="btn" @click="signIn">
          <span class="btn__text">Sign in</span>
        </button>
      </div>
    </main>
  </template>

  <!-- Generic auth error -->
  <template v-else-if="!loading && sessionError === 'auth-failed'">
    <main class="main-content main-content--basic">
      <div class="error-banner">
        <h1>Unable to create session</h1>
        <p>
          Something went wrong while creating your session. Please refresh the
          page and try again. If the problem persists, contact support.
        </p>
      </div>
    </main>
  </template>
</template>

<style lang="scss">
@use "@/scss/front/global";
@import url("//use.typekit.net/fis2cku.css");
</style>

<script>
import CookieConsent from "@/components/CookieConsent";
import DashboardHeader from "@/components/DashboardHeader";
import MainNav from "@/components/MainNav";
import PageLoader from "@/components/PageLoader";
import FormatDate from "@/mixins/FormatDate";
import FormatStr from "@/mixins/FormatStr";
import GetData from "@/mixins/GetData";
import AccountService from "@/services/AccountService";
import GatedService from "@/services/GatedService";
import MarketingWordPressService from "@/services/MarketingWordPressService";
import SalesforceService from "@/services/SalesforceService";
import Auth0Service from "@/services/Auth0Service";
import { mapAppFields } from "@/state/modules/app.js";
import { mapCompanyFields } from "@/state/modules/company.js";
import { mapEventsFields } from "@/state/modules/events";
import { mapFocalAreasFields } from "@/state/modules/focalAreas.js";
import { mapUserFields } from "@/state/modules/user.js";
import { createSessionCookie } from "@/mixins/Cookies";

export default {
  name: "CableLabs Account",

  components: {
    CookieConsent,
    DashboardHeader,
    MainNav,
    PageLoader,
  },

  mixins: [FormatDate, FormatStr, GetData],

  data() {
    return {
      fetching: false,
      sessionError: null,
    };
  },

  computed: {
    ...mapAppFields([
      "dataPulled",
      "firstVisitNewDashboard",
      "lastLogin",
      "loading",
      "userAuthenticated",
      "userAuthorized",
    ]),
    ...mapCompanyFields([
      "companyName",
      "companyType",
      "companyUserCount",
      "parentNdas",
      "vendorNdas",
    ]),
    ...mapEventsFields(["pastEvents", "upcomingEvents"]),
    ...mapFocalAreasFields(["focalAreas"]),
    ...mapUserFields(["vendorAwaitingApproval"]),
  },

  async mounted() {
    this.loading = true;

    const session = await this.createSession();

    if (!session) {
      await this.$router.push({
        path: "/sign-in",
      });

      this.loading = false;
      return;
    }

    const userGroups = await this.getUserAuthGroups();
    const userAuthorized = await this.authorizeUser(userGroups);

    if (!userAuthorized) {
      await this.$router.push({
        path: "/not-authorized",
      });

      this.loading = false;
      return;
    }

    await this.getUserData(userGroups);
    await this.getAppData();

    this.$gtm.trackView("My CableLabs Account", "/");

    if (localStorage.getItem("redirect")) {
      this.handleRedirect();
    }

    this.loading = false;
  },

  methods: {
    async getUserAuthGroups() {
      const groupRequest = await Auth0Service.getUserGroups();

      return groupRequest && Array.isArray(groupRequest?.data)
        ? groupRequest.data
        : [];
    },
    async authorizeUser(userGroups = []) {
      const hasGroups = userGroups.length !== 0;
      const hasAccess = this.userHasAccess(userGroups);

      this.userAuthorized = hasGroups && hasAccess;

      return this.userAuthorized;
    },
    signIn() {
      // Interactive login — clears the failed-silent-auth state and sends the
      // user through the Auth0 redirect flow.
      this.sessionError = null;
      this.$auth0.loginWithRedirect();
    },
    closeNotification(notification) {
      notification.currentTarget.parentNode.classList.add(
        "notification--hidden",
      );
    },
    waitForAuth0Ready() {
      // The auth0-vue plugin processes the redirect callback (code/state →
      // token exchange) asynchronously. `isLoading` flips to false once that
      // finishes. Calling getAccessTokenSilently() before then triggers a
      // silent-iframe auth that fails with login_required under ITP /
      // third-party-cookie blocking (Safari, Brave, etc.).
      if (!this.$auth0.isLoading.value) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const unwatch = this.$watch(
          () => this.$auth0.isLoading.value,
          (loading) => {
            if (!loading) {
              unwatch();
              resolve();
            }
          },
        );
      });
    },
    async createSession() {
      try {
        await this.waitForAuth0Ready();

        // Only ask for a token once the SDK has confirmed the user is
        // authenticated. If not, return false so the route's authGuard drives
        // loginWithRedirect() — avoids the failing silent-iframe fallback.
        if (!this.$auth0.isAuthenticated.value) {
          return false;
        }

        const token = await this.$auth0.getAccessTokenSilently();

        createSessionCookie(token);

        const cookieName = "auth0_vue_session_token";
        const cookies = document.cookie.split(";").map((c) => c.trim());
        const hasSessionCookie = cookies.some((c) =>
          c.startsWith(cookieName + "="),
        );

        if (!hasSessionCookie) {
          // Cookie write failed → most likely blocked by browser settings
          console.error(
            "Session cookie not found; cookies may be blocked for this site.",
          );
          this.sessionError = "cookies-blocked";
          return false;
        }

        this.userAuthenticated = true;

        const queryParams = { ...this.$route.query };
        delete queryParams.code;
        delete queryParams.state;
        this.$router.replace({ query: queryParams });

        this.sessionError = null; // clear any previous errors
        return true;
      } catch (error) {
        console.error("Error creating session:", error);

        if (
          error?.error === "login_required" ||
          String(error).includes("login_required")
        ) {
          this.sessionError = "login-required";
        } else {
          this.sessionError = "auth-failed";
        }

        return false;
      }
    },
    userHasAccess(groups) {
      return (
        this.$store.state.user.email.includes("cablelabs.com") ||
        groups.includes("confluence-access") ||
        groups.includes("cc-users") ||
        groups.includes("cl-members") ||
        groups.includes("cl-vendors") ||
        groups.includes("cl-employees") ||
        groups.includes("CableLabs Duo Employees") ||
        groups.includes("BTP_users")
      );
    },
    userIsEmployee() {
      return (
        this.$store.state.user.email.includes("cablelabs.com") ||
        this.$store.state.user.groups.includes("cl-employees") ||
        this.$store.state.user.groups.includes("CableLabs Duo Employees") ||
        this.$store.state.user.groups.includes("CableLabs Duo Users") ||
        this.$store.state.user.groups.includes("BTP_users")
      );
    },
    userIsVendor() {
      return (
        this.$store.state.user.groups.includes("cl-vendors") ||
        this.userIsEmployee()
      );
    },
    userIsMember() {
      return (
        this.$store.state.user.groups.includes("cl-members") ||
        this.$store.state.user.groups.includes("cc-users") ||
        this.$store.state.user.groups.includes("confluence-access") ||
        this.userIsEmployee()
      );
    },
    handleRedirect() {
      let redirectLink = localStorage.getItem("redirect");

      if (!redirectLink) {
        return;
      }

      localStorage.removeItem("redirect");

      const isFullLink =
        redirectLink.startsWith("https://") ||
        redirectLink.startsWith("http://");

      if (redirectLink.startsWith("/")) {
        redirectLink = redirectLink.replace(/^\//, "");
      }

      const port =
        location.hostname === "localhost" && location.port
          ? `:${location.port}`
          : "";

      if (!isFullLink) {
        window.location.href = `${location.protocol}//${location.hostname}${port}/${redirectLink}`;
        return;
      }

      window.location.href = redirectLink;
    },
    async getAppData() {
      await Promise.allSettled([
        await this.getCompanyCount(),
        await this.getCompanyData(),
        await this.getFocalAreas(),
        this.getLastLogin(),
        this.getNewDashboardVisit(),
        this.getEvents(),
      ]);
    },
    async getEvents() {
      let pastEventsResponse = [];
      let upcomingEventsResponse = [];

      const newDate = new Date();
      const todaysDate = new Date(newDate.toDateString());
      const oneYearAgo = new Date(
        newDate.setFullYear(newDate.getFullYear() - 1),
      );

      const wordPressResponse = await MarketingWordPressService.getEvents();

      if (wordPressResponse && wordPressResponse.data) {
        const events = wordPressResponse.data;

        for (let i = 0; i < events.length; i++) {
          if (events[i].acf.length <= 0) continue;

          let event = {};
          let startDate = events[i].acf.date?.start_date;
          const title = events[i].title?.rendered;

          if (!startDate || !title) continue;

          startDate =
            startDate.slice(0, 4) +
            "-" +
            startDate.slice(4, 6) +
            "-" +
            startDate.slice(6, 8);
          event.startDateInstance = new Date(startDate);

          // Don't include past events that are over one year old
          if (event.startDateInstance < oneYearAgo) continue;

          event.dateToDisplay = this.formatEventDate(
            events[i].acf.date?.start_date,
            events[i].acf.date?.end_date,
          );
          const venueName = events[i].acf.location?.venue_name;
          const eventFormat =
            events[i].acf.location?.format === "Virtual"
              ? "Virtual Event"
              : events[i].acf.location?.format;
          event.title = title;
          event.url = events[i].link;
          event.type = venueName
            ? venueName
            : eventFormat
              ? eventFormat
              : events[i].acf.location?.venue_city;
          event.image =
            events[i]._embedded?.[
              "wp:featuredmedia"
            ][0].media_details?.sizes?.medium_large?.source_url;

          if (event.startDateInstance < todaysDate) {
            pastEventsResponse.push(event);
          } else if (event.startDateInstance > todaysDate) {
            upcomingEventsResponse.push(event);
          }
        }
      }

      const gatedEventsResponse = await GatedService.events();
      if (gatedEventsResponse && gatedEventsResponse.data) {
        const gatedEvents = gatedEventsResponse.data.map((gatedEvent) => ({
          startDateInstance: new Date(gatedEvent.startDate),
          dateToDisplay: this.formatEventDate(
            gatedEvent.startDate,
            gatedEvent.endDate ? gatedEvent.endDate : gatedEvent.startDate,
          ),
          image: gatedEvent?.imageUrl,
          title: gatedEvent.title,
          url: gatedEvent?.url,
          type:
            gatedEvent.location === "Virtual"
              ? "Virtual Event"
              : gatedEvent.location,
        }));
        gatedEvents.filter(
          (gatedEvent) => new Date(gatedEvent.startDate) > oneYearAgo,
        );
        gatedEvents.forEach((gatedEvent) => {
          if (gatedEvent.startDateInstance < todaysDate) {
            pastEventsResponse.push(gatedEvent);
          } else if (gatedEvent.startDateInstance > todaysDate) {
            upcomingEventsResponse.push(gatedEvent);
          }
        });
      }

      this.pastEvents = pastEventsResponse.sort(
        (a, b) => b.startDateInstance - a.startDateInstance,
      );
      this.upcomingEvents = upcomingEventsResponse.sort(
        (a, b) => a.startDateInstance - b.startDateInstance,
      );
    },
    async getCompanyCount() {
      const response = await AccountService.companyUserCount(
        this.$store.state.user.username,
      );

      if (response && response.data) {
        this.companyUserCount = response.data[0]
          ? Number(response.data[0].companyUserCount) - 1
          : 0;
      }
    },
    async getFocalAreas() {
      if (!this.userIsEmployee() || this.vendorAwaitingApproval) {
        this.focalAreas = [];
      }

      const response = await GatedService.focalAreas();

      if (response && response.data) {
        let focalAreas = response.data;

        for (let i = 0; i < response.data.length; i++) {
          focalAreas[i].slug = this.slugify(focalAreas[i].focalArea);
        }

        if (!this.userIsVendor()) {
          focalAreas = focalAreas.filter(
            (focalArea) => !focalArea.slug.includes("vendor"),
          );
        }

        if (!this.userIsMember()) {
          focalAreas = focalAreas.filter(
            (focalArea) => !focalArea.slug.includes("member"),
          );
        }

        this.focalAreas = focalAreas;
      }
    },
    async getLastLogin() {
      const response = await AccountService.lastLogin(
        this.$store.state.user.username,
      );

      if (response && response.data) {
        this.lastLogin = response.data[0]
          ? this.getFormattedDate(response.data[0].lastLogin)
          : "";
      }
    },
    async getNewDashboardVisit() {
      // Check to see if the user has logged into the new dashboard before
      const lookupResponse = await AccountService.lastVisit(
        this.$store.state.user.username,
      );

      if (lookupResponse && lookupResponse.data) {
        this.firstVisitNewDashboard = !(
          lookupResponse.data[0] && lookupResponse.data[0].clLoginC
        );

        // If the user has not logged into the new dashboard before, save their
        // username to the database so we know in the future that they have
        if (this.firstVisitNewDashboard) {
          try {
            await AccountService.saveLastVisit({
              clLoginC: this.$store.state.user.username,
            });
          } catch (error) {
            console.error("Error saving last visit:", error);
          }
        }
      }
    },
    async getCompanyData() {
      if (!this.$store.state.company.accountId) {
        return;
      }

      const response = await SalesforceService.companyLookup({
        accountId: this.$store.state.company.accountId,
      });

      if (response && response.data) {
        this.companyName = response.data.companyName;
        this.companyType = response.data.companyType.toLowerCase();
        this.parentNdas = this.breakTagDelimStrToArr(response.data.parentNdas);
        this.vendorNdas = this.commaDelimStrToArr(response.data.vendorNdas);
        this.vendorAwaitingApproval =
          this.vendorNdas.length === 0 &&
          this.parentNdas.length === 0 &&
          this.companyType === "vendor";
      }
    },
    onClick() {
      this.$gtm.trackEvent({
        category: "Home Page",
        action: "click",
        label: "Home page click",
        value: 1,
      });
    },
  },
};
</script>
